<template>
	<div v-if="visible" class="fd-checklist" :class="[ clazz, errorClass ]" :style="gridStyle" ref="container">
		<template v-for="(li) in rearrangedList" :key="li.value">
			<span v-if="li.empty"></span>
			<label v-else class="can-wrap"><input type="checkbox" :checked="hasValue(li.value)" @change="checkboxChanged($event, li.value)" :disabled="ro"> {{ li.name }}</label>
		</template>
	</div>
</template>

<script setup lang="ts">
import {ref, computed, type CSSProperties, onMounted, onUnmounted} from "vue";
import useEditor, {type EditorProps, editorProps, createDataToScreenFunction, createScreenToDataFunction } from "@fd/editors/Editor.ts";
import {Checklist} from "@t/fd/editor/Checklist.ts";
import type {ListItemBase} from "@t/list/List.ts";
import {observeWidth} from "@/utils/Container.ts";

const uprops = defineProps({...editorProps});
const props = uprops as EditorProps;

const chl = computed<Checklist>(() => props.part as Checklist);
const dataToScreen = createDataToScreenFunction(chl.value.icast);
const screenToData = createScreenToDataFunction(chl.value.icast);

const {visible, value, clazz, errorClass, style, ro, listItems, localData, tabindexBinding } = useEditor(props);

const container = ref<HTMLDivElement|null>(null);
const containerWidth = observeWidth(container);

const columns = computed<number>(() => {
	if (typeof chl.value.columns === "number") return chl.value.columns;
	let cols = 1;
	let maxWidth = 0;
	for (let bp of chl.value.columns) {
		if (containerWidth.value >= bp.minWidth) {
			if (bp.minWidth > maxWidth) {
				maxWidth = bp.minWidth;
				cols = bp.value;
			}
		}
	}
	return cols;
});

const listWithMissingItems = computed<ListItemBase[]>(() => {
	const missingItems = listItemsForUnlistedValues.value;
	if (missingItems.length === 0) return listItems.value;	// no missing items
	return listItems.value.concat(missingItems);
});

function findIndexOfData(listValue:string):number {
	const screenRep = dataToScreen(listValue);
	if (Array.isArray(value.value)) {
		for (let i = 0; i < value.value.length; i++) {
			const item = dataToScreen(value.value[i]);
			if (item === screenRep) {
				return i;
			}
		}
	}
	return -1;	// not found
}

function checkboxChanged(event:Event, screenValue:string) {
	const state = (event.target as HTMLInputElement).checked;
	setValue(screenValue, state);
}

/**
 * Test if value v is in property array
 */
function hasValue(v:string):boolean {
	return Array.isArray(value.value) && findIndexOfData(v) >= 0;
}

function setValue(v:string, mustBeThere:boolean):void {
	if (!Array.isArray(value.value)) value.value = [];
	const idx = findIndexOfData(v);
	if (idx >= 0) {
		// Found
		if (!mustBeThere) {
			// Remove
			value.value.splice(idx, 1);
		}
	} else {
		// Not Found
		if (mustBeThere) {
			// Add
			value.value.push(screenToData(v));
		}
	}
}

interface ListItemOrEmpty extends ListItemBase {
	empty?: boolean;
}

const rearrangedList = computed<ListItemOrEmpty[]>(() => {
	const count = columns.value;
	const result: ListItemOrEmpty[] = [];
	const src = listWithMissingItems.value;
	const maxRows = Math.floor((src.length + count - 1)/count);
	for (let row = 0; row < maxRows; row++) {
		for (let col = 0; col < count; col++) {
			const colStartIdx = maxRows * col;
			const idx = row + colStartIdx;
			if (idx < src.length) {
				result.push(src[idx]);
			} else {
				// Need placeholder
				result.push({
					value: "empty-item-"+idx,
					name: "",
					empty: true,
				})
			}
		}
	}
	return result;
});

const gridStyle = computed(() => {
	const styles = [];
	styles.push(style.value);
	const fr:string[] = [];
	for (let i = 0;  i < columns.value; i++) fr.push("auto");
	const extra:CSSProperties = {};
	extra['grid-template-columns'] = fr.join(" ");
	styles.push(extra);
	return styles;
});

const listItemsForUnlistedValues = computed<ListItemBase[]>(() => {
	const u = unlistedValues.value;
	const lis:ListItemBase[] = [];
	if (u.length === 0) return lis;
	u.forEach((val) => lis.push({ value: val, name: val }));
	return lis;
});

const unlistedValues = computed<any[]>(() => {
	const val = value.value; // is converted to string for screen display
	const unlisted:any[] = [];
	if (Array.isArray(val)) {
		const lst = listItems.value;
		for (let item of val) {
			let found = false;
			const dataVal = dataToScreen(item);
			for (let li of lst) {
				const listVal = dataToScreen(li.value);
				if (dataVal === listVal) {	// Compare screen values
					found = true;
					break;
				}
			}
			if (!found) unlisted.push(item);
		}
	}
	return unlisted;
});

</script>

<style>
.fd-checklist {
	display: grid;
	grid-template-columns: auto;
	column-gap: var(--gap);
}
</style>